/**
 * monthRange.ts — the shared earnings MONTH-PICKER range maths (2026-08-26).
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: earnings insights.
 *
 * WHY: the admin Insights Earnings panel and the clinician "My earnings" card
 * offer the SAME picker — "This month (to date)", the past 12 calendar months,
 * and a custom from/to day range — and both serialise the window to
 * orders-service as `?from=&to=` ISO instants. The maths lives here as PURE
 * functions (`now` is always a parameter — no Date.now() reads) so the
 * calendar boundaries are pinned by unit specs and both surfaces serialise
 * identically.
 *
 * BOUNDARIES ARE UTC AND HALF-OPEN: a month is [1st 00:00:00.000Z, 1st of the
 * NEXT month 00:00:00.000Z) — the exclusive `to` means two adjacent months can
 * never both count an event landing exactly on the boundary instant. UTC (not
 * Europe/London) on purpose: a compensation window needs ONE unambiguous cut
 * that both ends of the wire compute identically, and a DST-shifted civil
 * midnight would make the March/October months overlap or gap depending on
 * which side derived them. "This month" uses the same exclusive next-month
 * `to` — nothing can be recorded in the future, so the figures read as
 * month-to-date without a non-deterministic `now` landing in the URL.
 */

/** The `?from=&to=` pair a picker choice serialises to (both ISO instants). */
export interface EarningsRange {
  from: string;
  to: string;
}

/** One entry in the picker's month `<select>`. */
export interface MonthOption {
  /** `THIS_MONTH`, or a past calendar month as `YYYY-MM`. */
  id: string;
  label: string;
}

/** The picker id for the current calendar month (rendered "to date"). */
export const THIS_MONTH = 'this';

/** The picker id for the custom from/to day-range mode (no range of its own). */
export const CUSTOM_RANGE = 'custom';

// UTC formatters so the labels match the UTC boundary maths above — a local
// formatter could name the wrong month within an hour of a month boundary.
const monthAndYear = new Intl.DateTimeFormat('en-GB', { month: 'long', year: 'numeric', timeZone: 'UTC' });
const monthOnly = new Intl.DateTimeFormat('en-GB', { month: 'long', timeZone: 'UTC' });

/** `Date.UTC` month index (0-based) → the option id's `YYYY-MM`. */
function monthId(year: number, monthIndex: number): string {
  // Round-trip through Date so a negative month index normalises (Jan − 1 →
  // December of the previous year).
  const start = new Date(Date.UTC(year, monthIndex, 1));
  return `${start.getUTCFullYear()}-${String(start.getUTCMonth() + 1).padStart(2, '0')}`;
}

/**
 * The picker's month choices for a given `now`: this month (to date) first,
 * then the past 12 calendar months, newest first. The custom-range entry is
 * the PICKER's own affordance (it has no range until days are chosen), so it
 * is deliberately not produced here.
 */
export function monthOptions(now: Date): MonthOption[] {
  const year = now.getUTCFullYear();
  const month = now.getUTCMonth();
  const options: MonthOption[] = [
    { id: THIS_MONTH, label: `This month (${monthOnly.format(new Date(Date.UTC(year, month, 1)))} to date)` },
  ];
  for (let back = 1; back <= 12; back += 1) {
    const start = new Date(Date.UTC(year, month - back, 1));
    options.push({ id: monthId(year, month - back), label: monthAndYear.format(start) });
  }
  return options;
}

/**
 * A month option id → its half-open UTC range. `THIS_MONTH` resolves against
 * `now`; a `YYYY-MM` id resolves to that calendar month. An unrecognised id
 * (never produced by {@link monthOptions}) falls back to the current month so
 * a stale deep-copied id can only ever widen to "this month", never throw.
 */
export function monthRange(id: string, now: Date): EarningsRange {
  let year = now.getUTCFullYear();
  let monthIndex = now.getUTCMonth();
  if (id !== THIS_MONTH) {
    const match = /^(\d{4})-(\d{2})$/.exec(id);
    if (match) {
      year = Number(match[1]);
      monthIndex = Number(match[2]) - 1;
    }
  }
  return {
    from: new Date(Date.UTC(year, monthIndex, 1)).toISOString(),
    to: new Date(Date.UTC(year, monthIndex + 1, 1)).toISOString(),
  };
}

/**
 * The server's explicit-window cap (orders-service `earningsQuery`): one leap
 * year. Mirrored here so the picker cannot build a range the boundary would
 * 400 — Apply just stays disabled instead.
 */
const MAX_WINDOW_DAYS = 366;
const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * A custom day range (two `<input type="date">` values, `YYYY-MM-DD`) → the
 * half-open UTC range covering BOTH named days: from = the first day's
 * midnight, to = the midnight AFTER the last day (inclusive-end semantics in
 * the UI, exclusive instant on the wire — consistent with the month ranges).
 * Returns null while either day is missing/unparseable, the range is
 * inverted, or the window exceeds the server's 366-day cap — the picker keeps
 * Apply disabled on null rather than sending a window the server would
 * reject.
 */
export function customRange(fromDay: string, toDay: string): EarningsRange | null {
  const dayPattern = /^\d{4}-\d{2}-\d{2}$/;
  if (!dayPattern.test(fromDay) || !dayPattern.test(toDay)) return null;
  const from = new Date(`${fromDay}T00:00:00.000Z`);
  const toStart = new Date(`${toDay}T00:00:00.000Z`);
  if (Number.isNaN(from.getTime()) || Number.isNaN(toStart.getTime())) return null;
  if (from.getTime() > toStart.getTime()) return null;
  const to = new Date(toStart.getTime());
  to.setUTCDate(to.getUTCDate() + 1);
  if (to.getTime() - from.getTime() > MAX_WINDOW_DAYS * DAY_MS) return null;
  return { from: from.toISOString(), to: to.toISOString() };
}
